import type { TimelineData } from '@/features/timeline/model/schema';
import type { TimelineCommand } from './timeline-editor-command';
import { createTimelineCommand } from './timeline-editor-command';
import type {
  TimelineCommandDto,
  TimelineEditorState,
} from './timeline-editor-types';

export const DEFAULT_HISTORY_LIMIT = 100;

const pushWithLimit = <T,>(items: T[], nextItem: T, limit: number): T[] => {
  if (limit <= 0) {
    return [];
  }
  const nextItems = [...items, nextItem];
  return nextItems.length <= limit ? nextItems : nextItems.slice(nextItems.length - limit);
};

interface HistoryEntry {
  undo: TimelineCommandDto;
  redo: TimelineCommandDto;
}

interface BatchCommandEntry {
  command: TimelineCommand;
  redo: TimelineCommandDto;
}

type PersistCommandBatch = (input: {
  changes: TimelineCommandDto[];
}) => Promise<TimelineData | null>;

interface SessionSnapshot {
  data: TimelineData;
  undoStack: HistoryEntry[];
  redoStack: HistoryEntry[];
}

export class TimelineEditorSession {
  private undoStack: HistoryEntry[] = [];

  private redoStack: HistoryEntry[] = [];

  constructor(
    private readonly timelineId: string,
    private current: TimelineData,
    private readonly historyLimit = DEFAULT_HISTORY_LIMIT,
  ) {}

  private createSnapshot(): SessionSnapshot {
    return {
      data: this.current,
      undoStack: [...this.undoStack],
      redoStack: [...this.redoStack],
    };
  }

  private restoreSnapshot(snapshot: SessionSnapshot): void {
    this.current = snapshot.data;
    this.undoStack = snapshot.undoStack;
    this.redoStack = snapshot.redoStack;
  }

  getCurrentData(): TimelineData {
    return this.current;
  }

  replaceCurrent(next: TimelineData): TimelineEditorState {
    this.current = next;
    this.undoStack = [];
    this.redoStack = [];
    return this.toState();
  }

  toState(): TimelineEditorState {
    return {
      timelineId: this.timelineId,
      data: this.current,
      canUndo: this.undoStack.length > 0,
      canRedo: this.redoStack.length > 0,
    };
  }

  async execute(
    command: TimelineCommand,
    redo: TimelineCommandDto,
    persistData: PersistCommandBatch,
    onOptimistic?: (state: TimelineEditorState) => void,
  ): Promise<TimelineEditorState | null> {
    const execution = command.execute(this.current);
    if (!execution) {
      return this.toState();
    }

    const snapshot = this.createSnapshot();

    this.current = execution.next;
    this.undoStack = pushWithLimit(this.undoStack, {
      undo: execution.inverse,
      redo,
    }, this.historyLimit);
    this.redoStack = [];
    onOptimistic?.(this.toState());

    try {
      const persisted = await persistData({
        changes: [redo],
      });
      if (!persisted) {
        this.restoreSnapshot(snapshot);
        return null;
      }

      this.current = persisted;
      return this.toState();
    } catch (error) {
      this.restoreSnapshot(snapshot);
      throw error;
    }
  }

  async executeBatch(
    entries: BatchCommandEntry[],
    persistData: PersistCommandBatch,
    onOptimistic?: (state: TimelineEditorState) => void,
  ): Promise<TimelineEditorState | null> {
    if (entries.length === 0) {
      return this.toState();
    }

    const snapshot = this.createSnapshot();
    const executedEntries: HistoryEntry[] = [];

    try {
      for (const entry of entries) {
        const execution = entry.command.execute(this.current);
        if (!execution) {
          throw new Error('Failed to execute timeline command batch');
        }

        this.current = execution.next;
        executedEntries.push({
          undo: execution.inverse,
          redo: entry.redo,
        });
      }
    } catch (error) {
      this.restoreSnapshot(snapshot);
      throw error;
    }

    if (executedEntries.length === 0) {
      return this.toState();
    }

    this.undoStack = executedEntries.reduce((stack, entry) => {
      return pushWithLimit(stack, entry, this.historyLimit);
    }, this.undoStack);
    this.redoStack = [];
    onOptimistic?.(this.toState());

    try {
      const persisted = await persistData({
        changes: executedEntries.map((entry) => entry.redo),
      });
      if (!persisted) {
        this.restoreSnapshot(snapshot);
        return null;
      }

      this.current = persisted;
      return this.toState();
    } catch (error) {
      this.restoreSnapshot(snapshot);
      throw error;
    }
  }

  async undo(
    persistData: PersistCommandBatch,
    onOptimistic?: (state: TimelineEditorState) => void,
  ): Promise<TimelineEditorState | null> {
    if (this.undoStack.length === 0) {
      return this.toState();
    }

    const entry = this.undoStack[this.undoStack.length - 1];
    const snapshot = this.createSnapshot();

    const undoCommand = createTimelineCommand(entry.undo);
    const execution = undoCommand.execute(this.current);
    if (execution) {
      this.current = execution.next;
    }
    this.undoStack = this.undoStack.slice(0, -1);
    this.redoStack = pushWithLimit(this.redoStack, entry, this.historyLimit);
    onOptimistic?.(this.toState());

    try {
      const persisted = await persistData({
        changes: [entry.undo],
      });
      if (!persisted) {
        this.restoreSnapshot(snapshot);
        return null;
      }

      this.current = persisted;
      return this.toState();
    } catch (error) {
      this.restoreSnapshot(snapshot);
      throw error;
    }
  }

  async redo(
    persistData: PersistCommandBatch,
    onOptimistic?: (state: TimelineEditorState) => void,
  ): Promise<TimelineEditorState | null> {
    if (this.redoStack.length === 0) {
      return this.toState();
    }

    const entry = this.redoStack[this.redoStack.length - 1];
    const snapshot = this.createSnapshot();

    const redoCommand = createTimelineCommand(entry.redo);
    const execution = redoCommand.execute(this.current);
    if (execution) {
      this.current = execution.next;
    }
    this.redoStack = this.redoStack.slice(0, -1);
    this.undoStack = pushWithLimit(this.undoStack, entry, this.historyLimit);
    onOptimistic?.(this.toState());

    try {
      const persisted = await persistData({
        changes: [entry.redo],
      });
      if (!persisted) {
        this.restoreSnapshot(snapshot);
        return null;
      }

      this.current = persisted;
      return this.toState();
    } catch (error) {
      this.restoreSnapshot(snapshot);
      throw error;
    }
  }
}
